// screens/LevelsScreen.js
// BuzzInApp — Pantalla de niveles y leaderboard
//
// Mejoras v2:
//   • Leaderboard con avatar circular (foto real o inicial del nombre)
//   • Top 3 con clase gold/silver/bronze para borde especial
//   • Nivel activo con línea vertical naranja y gradiente
//   • Perks del nivel activo con color acento

import React, { useEffect, useState } from "react";
import LevelBar from "../components/LevelBar";
import { USER_LEVELS, getUserLevel } from "../config/security";
import { getDocs, query, orderBy, limit, collection } from "../config/firebase";
import { db } from "../config/firebase";
import useTranslation from "../i18n/useTranslation";
import "./LevelsScreen.css";

// ─── Avatar con fallback a inicial ───────────────────────────────────────────
const Avatar = ({ photoURL, displayName }) => {
  const initial = displayName?.charAt(0)?.toUpperCase() ?? "?";
  return (
    <div className="levels-screen__lb-avatar">
      {photoURL ? (
        <img
          src={photoURL}
          alt={displayName}
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.nextElementSibling.style.display = "flex";
          }}
        />
      ) : null}
      <span style={{ display: photoURL ? "none" : "flex" }}>
        {initial}
      </span>
    </div>
  );
};

// ─── LevelsScreen ─────────────────────────────────────────────────────────────
const LevelsScreen = ({ authHook }) => {
  const { t } = useTranslation();
  const { user, profile } = authHook;
  const totalClicks = profile?.totalClicks ?? 0;

  const [leaderboard, setLeaderboard] = useState([]);
  const [lbLoading, setLbLoading]     = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const ref  = collection(db, "users");
        const snap = await getDocs(
          query(ref, orderBy("totalClicks", "desc"), limit(10))
        );
        setLeaderboard(
          snap.docs.map((d, i) => ({
            rank:        i + 1,
            uid:         d.id,
            displayName: d.data().displayName ?? "Usuario",
            photoURL:    d.data().photoURL,
            totalClicks: d.data().totalClicks ?? 0,
            level:       getUserLevel(d.data().totalClicks ?? 0),
          }))
        );
      } catch (err) {
        console.error("[LevelsScreen] leaderboard error:", err);
      } finally {
        setLbLoading(false);
      }
    };
    load();
  }, []);

  // Clase de borde para top 3
  const rankClass = (rank) => {
    if (rank === 1) return "levels-screen__lb-item--gold";
    if (rank === 2) return "levels-screen__lb-item--silver";
    if (rank === 3) return "levels-screen__lb-item--bronze";
    return "";
  };

  return (
    <div className="levels-screen">
      <header className="levels-screen__header">
        <h1 className="levels-screen__title">{t("levels.title")}</h1>
      </header>

      <div className="levels-screen__body">
        {/* Barra de nivel del usuario */}
        {profile && (
          <section>
            <LevelBar totalClicks={totalClicks} />
          </section>
        )}

        {/* Todos los niveles */}
        <section>
          <h2 className="levels-screen__section-title">{t("levels.perks")}</h2>
          <div className="levels-screen__level-list">
            {USER_LEVELS.map((lvl) => {
              const isCurrent = getUserLevel(totalClicks).level === lvl.level;
              return (
                <div
                  key={lvl.level}
                  className={`levels-screen__level-item ${isCurrent ? "levels-screen__level-item--active" : ""}`}
                >
                  <span className="levels-screen__level-emoji">{lvl.emoji}</span>
                  <div className="levels-screen__level-info">
                    <span className="levels-screen__level-name">{lvl.name}</span>
                    <span className="levels-screen__level-range">
                      {lvl.maxClicks === Infinity
                        ? `${lvl.minClicks}+ clicks`
                        : `${lvl.minClicks}–${lvl.maxClicks} clicks`}
                    </span>
                    <div className="levels-screen__level-perks">
                      {lvl.perks.map((perk) => (
                        <span key={perk} className="levels-screen__perk">
                          {t(`levels.perkDescriptions.${perk}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="levels-screen__current-badge">
                      {t("levels.currentLevel")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Leaderboard */}
        <section>
          <h2 className="levels-screen__section-title">{t("levels.leaderboardTitle")}</h2>

          {lbLoading ? (
            <div className="levels-screen__lb-loading">
              {[1,2,3].map((i) => (
                <div key={i} className="levels-screen__lb-skeleton" />
              ))}
            </div>
          ) : leaderboard.length === 0 ? (
            <p className="levels-screen__lb-empty">{t("levels.leaderboardEmpty")}</p>
          ) : (
            <ol className="levels-screen__lb-list">
              {leaderboard.map((entry) => (
                <li
                  key={entry.uid}
                  className={[
                    "levels-screen__lb-item",
                    entry.uid === user?.uid ? "levels-screen__lb-item--me" : "",
                    rankClass(entry.rank),
                  ].join(" ").trim()}
                >
                  <span className="levels-screen__lb-rank">
                    {entry.rank <= 3
                      ? ["🥇","🥈","🥉"][entry.rank - 1]
                      : `#${entry.rank}`}
                  </span>

                  <Avatar
                    photoURL={entry.photoURL}
                    displayName={entry.displayName}
                  />

                  <span className="levels-screen__lb-emoji">{entry.level.emoji}</span>

                  <span className="levels-screen__lb-name">
                    {entry.displayName}
                    {entry.uid === user?.uid && (
                      <span className="levels-screen__lb-you">
                        {t("levels.you")}
                      </span>
                    )}
                  </span>

                  <span className="levels-screen__lb-clicks">
                    {entry.totalClicks.toLocaleString()}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
};

export default LevelsScreen;